import { create } from 'zustand';
import createAxiosInstance from '../services/global/axiosInstance';
import {
  ICollectionAccount,
  IPayoutPayload,
  IPayoutSummary,
} from '../models/payout.model';
import errorHandler from '../utils/error-handler.util';
import { IApiResponse, IPagination } from '../models/resource.model';
import {
  IGetTransactionParam,
  ITransaction,
} from '../models/transaction.model';

const axiosInstance = createAxiosInstance();

export interface IPayoutStore {
  loading: boolean;
  payoutPayload: IPayoutPayload;
  payoutCollectionAccount: ICollectionAccount;
  setPayoutCollectionAccount: (payload: ICollectionAccount) => void;
  setPayoutPayload: (payload: IPayoutPayload) => void;
  resetPayoutPayload: () => void;
  payoutSummary: IPayoutSummary;
  setPayoutSummary: (summary: IPayoutSummary) => void;
  beneficiaryFormState: 'add_new' | 'select_existing' | 'selected_existing';
  setBeneficiaryFormState: (
    state: 'add_new' | 'select_existing' | 'selected_existing',
  ) => void;
  initiatePayout: () => Promise<IApiResponse | null>;
  getCollectionAccount: () => Promise<IApiResponse | null>;
  getOnrampingAccounts: () => Promise<IApiResponse | null>;
  getOtp: () => Promise<IApiResponse | null>;
  transactions: ITransaction[];
  setTransactions: (transactions: ITransaction[]) => void;
  getAllTransactions: (
    params: IGetTransactionParam,
  ) => Promise<IApiResponse | null>;
  transactionsPagination: IPagination;
  setTransactionsPagination: (paginationObj: IPagination) => void;
}

const usePayoutStore = create<IPayoutStore>((set, get) => ({
  loading: false,
  payoutPayload: {
    beneficiaryId: '',
    negotiateRateId: '',
    paymentPurpose: 'Personal',
    fromCurrencyCode: '',
    toCurrencyCode: '',
    amount: 0,
    toAmount: 0,
    proofOfPaymentDocumentId: '',
    verificationCode: '',
  },
  setPayoutPayload: (payload: IPayoutPayload) => {
    set({ payoutPayload: { ...payload } });
  },
  resetPayoutPayload: () => {
    set({
      payoutPayload: {
        beneficiaryId: '',
        negotiateRateId: '',
        paymentPurpose: 'Personal',
        fromCurrencyCode: '',
        toCurrencyCode: '',
        amount: 0,
        toAmount: 0,
        proofOfPaymentDocumentId: '',
        verificationCode: '',
      },
    });
  },
  payoutCollectionAccount: {
    id: '',
    accountNumber: '',
    accountName: '',
    bankName: '',
    currencyCode: '',
    maximumAmount: 0,
    enabled: false,
  },
  setPayoutCollectionAccount: (payload: ICollectionAccount) => {
    set({ payoutCollectionAccount: { ...payload } });
  },
  payoutSummary: {
    exchangeRateText: '',
    sendingAmountText: '',
    receivingAmountText: '',
  },
  setPayoutSummary: (sumary: IPayoutSummary) => {
    set({ payoutSummary: { ...sumary } });
  },
  beneficiaryFormState: 'select_existing',
  setBeneficiaryFormState: (
    state: 'add_new' | 'select_existing' | 'selected_existing',
  ) => {
    set({ beneficiaryFormState: state });
  },
  initiatePayout: async () => {
    set({ loading: true });
    try {
      const payload = get().payoutPayload;
      const response = await axiosInstance.post(
        '/corporate/transaction',
        {
          ...payload,
          negotiateRateId: null,
        },
        {
          headers: {
            'Idempotency-Key': crypto.randomUUID(), // Generate a unique key
          },
        },
      );
      set({ loading: false });
      return response.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  getCollectionAccount: async () => {
    const payload = get().payoutPayload;
    set({ loading: true });
    try {
      const response = await axiosInstance.get(
        `/backoffice/local-onramping-accounts/${payload.toCurrencyCode}`,
      );
      set({ loading: false });
      return response.data.value;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  getOnrampingAccounts: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(
        '/backoffice/local-onramping-accounts',
        {
          params: {
            Page: 1,
            PageSize: 10,
            SortColumn: '',
            sortOrder: '',
            name: '',
          },
        },
      );
      set({
        loading: false,
      });
      return response.data.value;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  getOtp: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/corporate/otp`);
      set({ loading: false });
      return response.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  transactions: [],
  setTransactions: (transactions: ITransaction[]) => {
    set({ transactions });
  },
  getAllTransactions: async ({
    page = 1,
    pageSize = 10,
    startDate,
    endDate,
    status,
    beneficiaryName,
    sortColumn,
    sortOrder,
  }: IGetTransactionParam): Promise<IApiResponse | null> => {
    set({ loading: true });

    try {
      const response = await axiosInstance.get(`/corporate/transactions`, {
        params: {
          page,
          pageSize,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(status ? { status } : {}), // Include status if it exists
          ...(beneficiaryName ? { beneficiaryName } : {}), // Include beneficiaryName if it exists
          ...(sortColumn ? { sortColumn } : {}), // Include sortColumn if it exists
          ...(sortOrder ? { sortOrder } : {}), // Include sortOrder if it exists
        },
      });

      if (response?.data?.value) {
        get().setTransactionsPagination({
          currentPage: response.data.value.page,
          totalRecords: response.data.value.totalCount,
          currentPageSize: response.data.value.pageSize,
          hasNextPage: response.data.value.hasNextPage,
          hasPrevious: response.data.value.hasPrevious,
        });
        get().setTransactions(response.data.value.items);
      }

      return response.data;
    } catch (err: any) {
      errorHandler(err);
      return {
        value: null,
        isSuccess: false,
        isFailure: true,
        error: err,
      };
    } finally {
      set({ loading: false });
    }

    return null;
  },
  transactionsPagination: {
    currentPage: 1,
    totalRecords: 0,
    currentPageSize: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevious: true,
  },
  setTransactionsPagination: (paginationObj: IPagination) => {
    set({ transactionsPagination: { ...paginationObj } });
  },
}));

export default usePayoutStore;
